/**
 * Auto-collapse for {@link SidebarRail}.
 *
 * Kills the squeezed desktop band: below {@link RAIL_AUTOCOLLAPSE_MAX_WIDTH} the
 * expanded rail no longer leaves room for the chat's full 680px lane, so the
 * rail is forced to icon-only (the chat then keeps its 680px lane all the way
 * down to the 768px mobile cut). Collapse acts ONLY when the viewport CROSSES
 * the boundary, so a manual expand inside the narrow band is honored until the
 * next crossing, and widening restores the saved preference.
 *
 * The decision (`resolveRailExpansionForWidth`) is a pure function so the
 * boundary-crossing state machine is testable in isolation; the hook only wires
 * it to the window resize event.
 */
import { useEffect, useRef } from 'react';

import { BREAKPOINTS } from '@/lib/breakpoints';

/**
 * The width below which the expanded rail can no longer leave the chat its full
 * 680px lane. Lives in the shared breakpoint registry — the fit derivation
 * (expanded rail + themed float + chat lane + desk gutters) is documented on
 * `BREAKPOINTS.railExpandedMin` in `lib/breakpoints.ts`.
 */
export const RAIL_AUTOCOLLAPSE_MAX_WIDTH = BREAKPOINTS.railExpandedMin;

const SIDEBAR_EXPANDED_STORAGE_KEY = 'xavior:sidebarExpanded';

/**
 * The user's saved expand/collapse preference. Defaults to expanded — only an
 * explicit `'false'` collapses. Guarded so a throwing `localStorage` (private
 * mode, blocked storage) falls back to expanded.
 */
export function readSidebarExpandedPreference(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_EXPANDED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

/**
 * The rail's INITIAL expanded state: the saved preference clamped by the
 * current viewport. Without the clamp, mounting inside the narrow band with a
 * saved expanded preference paints the expanded rail for one frame and then
 * animates it closed (`transition: width .2s`) when the auto-collapse effect
 * runs post-paint — a visible expand→collapse flash on load.
 */
export function initialRailExpansion(): boolean {
  const saved = readSidebarExpandedPreference();
  if (typeof window === 'undefined') return saved;
  return resolveRailExpansionForWidth(window.innerWidth, null, saved)?.expanded ?? saved;
}

/**
 * Decide the rail's expanded state for a viewport width.
 *
 * Returns the next expanded value ONLY when the viewport crosses the narrow
 * boundary (so the auto-collapse never fights a manual expand inside the band);
 * returns `null` when the width stays on the same side of the boundary as the
 * previous evaluation (`wasNarrow`) — the caller should make no change.
 *
 * `wasNarrow` is `null` on the first evaluation, which always counts as a
 * crossing so the initial width is applied.
 *
 * @param width - current viewport width (e.g. `window.innerWidth`)
 * @param wasNarrow - whether the previous evaluation was in the narrow band
 * @param savedPreference - the user's saved expand preference (see {@link readSidebarExpandedPreference})
 * @returns the next state on a crossing, or `null` for no change
 */
export function resolveRailExpansionForWidth(
  width: number,
  wasNarrow: boolean | null,
  savedPreference: boolean,
): { expanded: boolean; isNarrow: boolean } | null {
  const isNarrow = width < RAIL_AUTOCOLLAPSE_MAX_WIDTH;
  if (wasNarrow === isNarrow) return null;
  return { isNarrow, expanded: isNarrow ? false : savedPreference };
}

/**
 * Auto-collapse the sidebar rail to icon-only when the viewport is too narrow
 * for the chat's full 680px lane, restoring the saved preference when it widens
 * back. Acts only on boundary crossings — see {@link resolveRailExpansionForWidth}.
 *
 * @param setExpanded - the rail's expanded-state setter (stable; from `useState`)
 */
export function useRailAutoCollapse(setExpanded: (expanded: boolean) => void): void {
  // Tracks which side of the boundary the last evaluation landed on, so a
  // resize within the same band is a no-op (and a manual expand survives it).
  const wasNarrowRef = useRef<boolean | null>(null);
  useEffect(() => {
    const onResize = () => {
      const next = resolveRailExpansionForWidth(
        window.innerWidth,
        wasNarrowRef.current,
        readSidebarExpandedPreference(),
      );
      if (!next) return;
      wasNarrowRef.current = next.isNarrow;
      setExpanded(next.expanded);
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [setExpanded]);
}
